import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Instagram, Facebook, Mail, Plus, ChevronDown } from "lucide-react";
import Navigation from "./navigationLinks";
import backgroundIMG from "../images/mehndiBackground.png";
import logo from "../images/logo.png";
import styles from "./Navbar.module.css";

const useViewport = () => {
  const [viewport, setViewport] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const updateViewport = () => {
      setViewport((prev) => ({
        width: window.innerWidth,
        height: prev.height || window.innerHeight,
      }));
    };

    updateViewport();
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  return viewport;
};

const Navbar = ({ isHomeShrunk, onRevealClick }) => {
  const { pathname } = useLocation();
  const { width } = useViewport();
  const isMobile = width <= 768;
  const isHomeRoute = pathname === "/";
  const isAlwaysShrunk = !isHomeRoute;
  const isShrunk = !isHomeRoute || isHomeShrunk;
  const [showSocialLinks, setShowSocialLinks] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDesktopSocial, setShowDesktopSocial] = useState(false);
  const [isShrinking, setIsShrinking] = useState(false);
  const shrinkTimeoutRef = useRef(null);

  const mobileHeaderHeight = 188;
  const shrunkHeight = 190;

  const logoScale = isMobile
    ? 0.5
    : isShrunk
      ? 0.3
      : 0.65;

  useEffect(() => {
    if (!isHomeShrunk && isExpanded) {
      setIsExpanded(false);
    }
  }, [isHomeShrunk, isExpanded]);

  useEffect(() => {
    if (pathname !== "/") {
      setIsExpanded(false);
    }
  }, [pathname]);

  useEffect(() => {
    setShowSocialLinks(false);
  }, [pathname]);

  useEffect(() => {
    return () => {
      if (shrinkTimeoutRef.current) {
        clearTimeout(shrinkTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const shouldLockPageScroll = isHomeRoute && !isMobile && !isShrunk;
    let touchStartY = 0;

    const triggerShrink = () => {
      if (isShrunk || isShrinking) {
        return;
      }
      setIsShrinking(true);
      onRevealClick();
      if (shrinkTimeoutRef.current) {
        clearTimeout(shrinkTimeoutRef.current);
      }
      shrinkTimeoutRef.current = setTimeout(() => {
        setIsShrinking(false);
      }, 1250);
    };

    if (shouldLockPageScroll || isShrinking) {
      document.body.classList.add("page-scroll-locked");
      window.scrollTo({ top: 0, behavior: "auto" });

      const revealFromScrollIntent = (event) => {
        event.preventDefault();
        triggerShrink();
      };

      const handleWheel = (event) => {
        if (event.deltaY > 0) {
          revealFromScrollIntent(event);
        }
      };

      const handleTouchStart = (event) => {
        touchStartY = event.touches[0]?.clientY || 0;
      };

      const handleTouchMove = (event) => {
        const currentY = event.touches[0]?.clientY || 0;
        const swipeUpDistance = touchStartY - currentY;
        if (swipeUpDistance > 12) {
          revealFromScrollIntent(event);
        }
      };

      window.addEventListener("wheel", handleWheel, { passive: false });
      window.addEventListener("touchstart", handleTouchStart, { passive: true });
      window.addEventListener("touchmove", handleTouchMove, { passive: false });

      return () => {
        window.removeEventListener("wheel", handleWheel);
        window.removeEventListener("touchstart", handleTouchStart);
        window.removeEventListener("touchmove", handleTouchMove);
        document.body.classList.remove("page-scroll-locked");
      };
    } else {
      document.body.classList.remove("page-scroll-locked");
    }

    return () => {
      document.body.classList.remove("page-scroll-locked");
    };
  }, [isHomeRoute, isMobile, isShrunk, isShrinking, onRevealClick]);

  useEffect(() => {
    if (isMobile || isShrunk) {
      setShowDesktopSocial(false);
      return undefined;
    }

    const timer = setTimeout(() => {
      setShowDesktopSocial(true);
    }, 1200);

    return () => clearTimeout(timer);
  }, [isMobile, isShrunk]);

  const handleToggleExpand = () => {
    setIsExpanded(prev => {
      const next = !prev;
      if (next || isHomeShrunk) {
        onRevealClick();
      }
      return next;
    });
  };

  return (
    <>
      <motion.div
        className={`${styles.navbarContainer} ${
          isShrunk ? styles.shrunk : ""
        } ${isMobile ? styles.mobileFixed : ""} ${
          isAlwaysShrunk ? styles.alwaysShrunk : ""
        }`}
        initial={false}
        animate={
          isMobile
            ? undefined
            : { height: isShrunk ? `${shrunkHeight}px` : "100vh" }
        }
        transition={{ duration: 1.2, ease: [0.65, 0, 0.35, 1] }}
        style={isMobile ? { height: `${mobileHeaderHeight}px` } : undefined}
      >
      <div className={styles.socialLinks}>
        <AnimatePresence mode="wait">
          {(isMobile || isShrunk) && (
            <motion.div
              key="arc-trigger"
              className={styles.arcWrapper}
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.25, ease: [0.65, 0, 0.35, 1] }}
            >
              <motion.div
                className={`${styles.iconWrapper} ${styles.plus}`}
                onClick={(e) => {
                  setShowSocialLinks((prev) => !prev);
                }}
                animate={{ rotate: showSocialLinks ? 45 : 0 }}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
                whileHover={{ scale: 1.2 }}
              >
                <Plus size={16} strokeWidth={1.5} color="white" />
              </motion.div>

              <AnimatePresence>
                {showSocialLinks && (
                  <motion.div
                    className={styles.arcContainer}
                    initial={{ opacity: 0, scale: 0.96, y: 4 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96, y: 4 }}
                    transition={{ duration: 0.28, ease: [0.65, 0, 0.35, 1] }}
                  >
                    {[
                      {
                        href: "https://www.instagram.com/simmi.mehndi",
                        className: styles.instagram,
                        icon: <Instagram size={16} strokeWidth={1.5} color="white" />, delay: 0,
                        initialX: 0,
                        initialY: -32,
                        x: 64,
                        y: -32,
                      },
                      {
                        href: "https://www.facebook.com/simmiravi16/",
                        className: styles.facebook,
                        icon: <Facebook size={16} strokeWidth={1.5} color="white" />, delay: 0.1,
                        x: 48,
                        y: -18,
                        initialX: 0,
                        initialY: -64,
                      },
                      {
                        href: "mailto:simmi.mehndi@gmail.com",
                        className: styles.email,
                        icon: <Mail size={16} strokeWidth={1.5} color="white" />, delay: 0.15,
                        x: 0,
                        y: -32,
                        initialX: 0,
                        initialY: -96,
                      }
                    ].map((link, index) => (
                      <motion.a
                        key={index}
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${styles.iconWrapper} ${styles.arc} ${link.className}`}
                        initial={{ x: link.initialX, y: link.initialY, opacity: 0, pointerEvents: "none" }}
                        animate={{ x: link.x, y: link.y, opacity: 1, pointerEvents: "auto" }}
                        exit={{ x: link.initialX, y: link.initialY, opacity: 0, pointerEvents: "none" }}
                        transition={{
                          duration: 0.4,
                          delay: link.delay,
                          ease: [0.65, 0, 0.35, 1],
                          type: "spring",
                          stiffness: 500,
                          damping: 30
                        }}
                        whileHover={{ scale: 1.2 }}
                      >
                        {link.icon}
                      </motion.a>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {!isMobile && isHomeRoute && (
        <motion.button
          className={styles.expandButton}
          onClick={handleToggleExpand}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.4, ease: [0.65, 0, 0.35, 1] , type: "spring", stiffness: 500, damping: 30}}
              whileHover={{ scale: 1.1 }}
        >
          <ChevronDown
  size={16}
  strokeWidth={1.5}
  className={`${styles.arrowIcon} ${!isHomeShrunk ? styles.rotated : ""}`}
/>

        </motion.button>
      )}

      <img
        src={backgroundIMG}
        alt="mehndiImage"
        className={styles.backgroundImage}
      />

      <motion.img
        src={logo}
        alt="simmiLogo"
        initial={{ scale: 0.9, y: "-50%", x: "-50%", opacity: 0 }}
        animate={{
          scale: logoScale,
          y: isMobile ? "-50%" : "-51%",
          x: "-50%",
          opacity: 1,
          transition: { duration: 1.2, ease: [0.65, 0, 0.35, 1] },
        }}
        style={{
          position: "absolute",
          top: isMobile ? "42%" : isShrunk ? "44%" : "50%",
          left: "50%",
          zIndex: 80,
          margin: 0,
          maxWidth: isMobile ? "90vw" : "none",
        }}
      />

      <AnimatePresence>
        {!isMobile && !isShrunk && showDesktopSocial && (
          <motion.div
            className={styles.desktopCenteredSocial}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.35, ease: [0.65, 0, 0.35, 1] }}
          >
            <a
              href="https://www.instagram.com/simmi.mehndi"
              target="_blank"
              rel="noopener noreferrer"
              className={`${styles.iconWrapper} ${styles.instagram}`}
            >
              <Instagram size={16} strokeWidth={1.5} color="white" />
            </a>
            <a
              href="https://www.facebook.com/simmiravi16/"
              target="_blank"
              rel="noopener noreferrer"
              className={`${styles.iconWrapper} ${styles.facebook}`}
            >
              <Facebook size={16} strokeWidth={1.5} color="white" />
            </a>
            <a
              href="mailto:simmi.mehndi@gmail.com"
              className={`${styles.iconWrapper} ${styles.email}`}
            >
              <Mail size={16} strokeWidth={1.5} color="white" />
            </a>
          </motion.div>
        )}
      </AnimatePresence>

        <Navigation />
      </motion.div>
      {isMobile && <div style={{ height: `${mobileHeaderHeight}px` }} />}
    </>
  );
};

export default Navbar;
